/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-study-cohorts.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';

import { useAuthorization, useTranslation, useBoolean, useSysAdminCanEditStudy } from 'hooks';
import { GET_STUDY_COHORTS } from 'queries/study-cohorts';
import NotificationService from 'utils/notification';
import AddCohortDialog from 'views/components/admin/add-cohort';
import AdminStudyCohortsGrid from 'views/components/admin/admin-study-cohorts-grid';
import Loading from 'views/components/core/loading';
import Error from 'views/components/error/error';
import Constants from '@utils/constants';

interface AdminStudyCohortsInterface {
    studyId: string;
    canEdit: boolean;
}

const AdminStudyCohorts = ({ studyId, canEdit }: AdminStudyCohortsInterface) => {
    const user = useAuthorization();
    const isSysAdmin = user.isSystemAdmin(Constants.SystemAdminLevel.SysAdmin);
    const { t } = useTranslation();
    const { loading, error, data } = useQuery(GET_STUDY_COHORTS, { variables: { studyId } });
    const [cohorts, setCohort] = useState([]);
    const [cohortsData, setCohortData] = useState([]);
    const [currentCohort, setCurrentCohort] = useState(null);
    const openAddCohort = useBoolean(false);
    const sysAdminCanEditStudy = useSysAdminCanEditStudy();
    useEffect(() => {
        if (data?.cohorts) setCohortData(data?.cohorts);
    }, [data]);

    if (!user || !user.systemAdminLevel) return null;

    if (!studyId || loading) return <Loading />;

    if (error) return <Error error={error} />;

    return (
        <>
            <AddCohortDialog
                open={openAddCohort.value}
                cohort={currentCohort}
                cohorts={cohorts}
                studyId={studyId}
                onClose={(action: string | boolean | undefined) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add') NotificationService.showNotification(t('admin:cohortSuccessfulCreate'));
                        else if (action === 'edit')
                            NotificationService.showNotification(
                                t('admin:cohortSuccessfulUpdate', { id: currentCohort?.recordId }),
                            );
                        openAddCohort.setFalse();
                        setCurrentCohort(null);
                    }
                }}
                isSysAdmin={isSysAdmin}
            />
            <AdminStudyCohortsGrid
                canEdit={canEdit}
                sysAdminCanEditStudy={sysAdminCanEditStudy}
                cohortsData={cohortsData}
                cohorts={cohorts}
                openAddCohort={openAddCohort}
                setCohort={setCohort}
                setCurrentCohort={setCurrentCohort}
            />
        </>
    );
};

export default AdminStudyCohorts;
